import React, {useCallback, useEffect, useState} from "react";
// Navigation Drawer
import {
  AppBar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {Link, useLocation, useNavigate} from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {useAuth} from "../../context/AuthContext";
import {useNav} from "../../context/NavContext";
import {ExpandLess, ExpandMore} from "@mui/icons-material";

// Navigation items configuration
export const navigationItems = [
  {
    path: "/",
    label: "Home",
    icon: "🏠",
    description: "Dashboard overview",
    borderBottom: false,
  },
  {
    path: "/g-risk",
    label: "G-Risk",
    icon: "⚠️",
    description: "Risk pages",
    borderBottom: false,
    borderTop: false,
  },
  {
    label: "MAB",
    icon: "📊",
    description: "Merger Arbitrage Group",
    borderTop: false,
    children: [
      {
        path: "/MAB_PNL",
        label: "MAB PNL",
        description: "Merger arbitrage performance",
      },
      {
        path: "/MAB_Risk",
        label: "MAB Risk",
        description: "Risk management",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "MAB PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Event Driven Credit",
    icon: "🎯",
    description: "Event-driven credit",
    borderTop: false,
    children: [
      {
        path: "/EventDrivenCredit_PNL",
        label: "Event Driven Credit PNL",
        description: "Event-driven credit performance",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "Event Driven Credit PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Converts",
    description: "Convertible bonds",
    icon: "🎯",
    borderBottom: false,
    borderTop: false,
    children: [
      {
        path: "/Converts_PNL",
        label: "Converts PNL",
        description: "Convertible bonds",
        icon: "🎯",
        borderBottom: false,
        borderTop: false,
      },
      {
        path: "/Converts_Blotter",
        label: "Converts Blotter",
        description: "Convertible bonds",
        icon: "🎯",
        borderBottom: false,
        borderTop: false,
      },
      {
        path: "/Converts_Scanner",
        label: "Converts Scanner",
        description: "Convertible bonds scanner",
        icon: "🎯",
        borderBottom: false,
        borderTop: false,
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "Converts PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Credit Arbitrage",
    icon: "🎯",
    description: "Credit Arbitrage",
    borderTop: false,
    children: [
      {
        path: "/CreditArbitrage",
        label: "Credit Arbitrage PNL",
        description: "Credit arbitrage performance",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "Credit Arbitrage PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "SPACs",
    icon: "🎯",
    description: "SPAC performance",
    borderTop: false,
    children: [
      {
        path: "/SPACS_PNL",
        label: "SPACs PNL",
        description: "SPAC performance",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "SPACs PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Quant Strategies",
    icon: "🎯",
    description: "Quantitative strategies",
    borderTop: false,
    children: [
      {
        path: "/StatArb",
        label: "Stat Arb PNL",
        description: "Stat arbitrage performance",
      },
      {
        path: "/VolArb",
        label: "Vol Arb PNL",
        description: "Vol arbitrage performance",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "Quant Strategies PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Other Strategies",
    icon: "🎯",
    description: "Additional strategies",
    borderTop: false,
    children: [
      {
        path: "/Other_Strategies",
        label: "Other Strategies",
        description: "Additional strategies",
      },
      {
        path: "https://data360.glazercapital.com/pnl-analysis",
        label: "data360 PnL Analysis",
        description: "Other Strategies PnL analysis on Data360",
        external: true,
      },
    ],
  },
  {
    label: "Analytics",
    icon: "🔍",
    description: "Analytics tools",
    borderTop: false,
    children: [
      {
        path: "https://nice-stone-05aeede0f.6.azurestaticapps.net/",
        label: "DMA AI",
        description: "Analyze DMAs",
        external: true,
      },
      {
        path: "/MAB_News",
        label: "MAB News",
        description: "MAB News",
      },
      {
        path: "/MAB_Scraping",
        label: "MAB Scraping",
        description: "MAB Scraping",
      },
      {
        path: "https://data360.glazercapital.com/filings-ai",
        label: "Filings AI",
        description: "AI-powered filings analysis",
        external: true,
      },
      {
        path: "https://data360.glazercapital.com/byod-redline",
        label: "BYOD Redline",
        description: "Bring your own document redline",
        external: true,
      },
    ],
  },
  {
    label: "Operations",
    icon: "⚙️",
    description: "Operations",
    borderTop: false,
    children: [
      {
        path: "/OpsPage",
        label: "Dashboard",
        description: "Operations dashboard",
      },
      {
        path: "/FlashPnl",
        label: "Archived Flash PnL",
        description: "Archive runs in the morning; PnL as of previous day",
      },
      {
        path: "https://data360.glazercapital.com",
        label: "Data360",
        description: "Data360",
        external: true,
      },
    ],
  },
  {
    label: "Trading",
    icon: "📈",
    description: "Trading tools",
    borderTop: false,
    children: [
      {
        path: "/historical-trade-search",
        label: "Trades Archive",
        description: "Trades Archive",
      },
    ],
  },
  {
    label: "Compliance",
    icon: "⚖️",
    description: "Compliance tools",
    borderTop: false,
    children: [
      {
        path: "/reg-m-preclear",
        label: "Reg M PreClear",
        description: "Regulation M pre-clearance attestations",
      },
    ],
  },
  {
    label: "Admin",
    icon: "📊",
    description: "Administrative tools",
    borderTop: false,
    children: [
      {
        path: "/SecurityMaster",
        label: "Security Master",
        icon: "🔒",
        description: "Security management",
        borderBottom: false,
      },
      {
        path: "/SodPositions",
        label: "SoD Positions",
        icon: "🔒",
        description: "Start of Day Positions",
        borderBottom: false,
      },
      {
        path: "/StrategyRegions",
        label: "Strategy Regions",
        icon: "🔒",
        description: "Strategy Regions",
        borderBottom: false,
      },
    ],
  },
];

export const NavigationDrawer = ({
  open,
  onClose,
  drawerWidth = 280,
  items = navigationItems,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState({});

  const childIsActive = useCallback((child) => {
    if (!child?.path || child.external) return false;
    if (child.path === "/g-var") {
      return location.pathname === "/g-var" || location.pathname.startsWith("/g-var/");
    }
    return location.pathname === child.path;
  }, [location.pathname]);

  useEffect(() => {
    const parent = items.find((item) => item.children?.some(childIsActive));
    if (!parent) return;
    setExpanded((prev) => (prev[parent.label] ? prev : { ...prev, [parent.label]: true }));
  }, [items, childIsActive]);

  const toggleExpand = (label) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNavClick = () => {
    setExpanded({});
    onClose();
  };

  const renderItem = (item, isSub = false) => {
    const isActive = item.path === "/g-risk"
      ? location.pathname === "/g-risk"
        || location.pathname.startsWith("/g-risk/")
        || location.pathname === "/g-var"
        || location.pathname.startsWith("/g-var/")
      : childIsActive(item);

    return (
      <ListItem
        disablePadding
        sx={{ mb: 0.5, pl: isSub ? 4 : 0 }}
        key={item.path}
      >
        <ListItemButton
          component={item.external ? "a" : Link}
          {...(item.external
            ? { href: item.path, target: "_blank", rel: "noopener noreferrer" }
            : { to: item.path })}
          onClick={handleNavClick}
          sx={{
            borderBottom: item.borderBottom ? "4px solid" : "none",
            borderTop: item.borderTop ? "4px solid" : "none",
            borderRadius: 2,
            mx: 0.5,
            backgroundColor: isActive
              ? theme.palette.primary.main + "15"
              : "transparent",
            color: isActive
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            "&:hover": {
              backgroundColor: isActive
                ? theme.palette.primary.dark + "25"
                : theme.palette.grey[100],
            },
            transition: "all 0.2s ease",
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: ".875rem",
              fontWeight: isActive ? 800 : 600,
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
              color: isActive
                ? theme.palette.primary.contrastText
                : theme.palette.text.secondary,
            }}
          />
          {isActive && (
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.725rem",
                backgroundColor: theme.palette.text.secondary,
                color: theme.palette.primary.contrastText,
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.grey[200]}`,
          boxShadow: theme.shadows[1],
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          minHeight: 64,
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src="/header-logo.svg"
            alt="Logo"
            style={{ height: 32, width: "auto" }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Glazer App
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          {theme.direction === "ltr" ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </Box>

      {/* Navigation — scrollable middle */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List sx={{ p: 1 }}>
          {items.map((item, index) => {
            const hasChildren = !!item.children;
            const isExpanded = expanded[item.label];

            return (
              <React.Fragment key={item.label}>
                {!hasChildren ? (
                  renderItem(item)
                ) : (
                  <>
                    <ListItemButton
                      onClick={() => toggleExpand(item.label)}
                      sx={{
                        mx: 0.5,
                        borderTop: item.borderTop ? "4px solid" : "none",
                        borderBottom: item.borderBottom ? "4px solid" : "none",
                        borderRadius: 2,
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: 700,
                        }}
                      />
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {item.children.map((child) => renderItem(child, true))}
                      </List>
                    </Collapse>
                  </>
                )}
                {index < items.length - 1 && <Divider sx={{ my: 1, mx: 2 }} />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

// Responsive AppBar
export const ResponsiveAppBar = ({
  onMenuClick,
  title = "Glazer App",
  subtitle,
  actions,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { claims } = useAuth();
  const name = claims?.name;
  subtitle = `Welcome, ${name}`;

  // Live fund NAV shown in the top-right of the header — single source of
  // truth via NavContext (fetched once by NavProvider, shared app-wide).
  const nav = useNav();

  // Check if we're in development session
  const isDevSession = process.env.REACT_APP_DEV_SESSION === "TRUE";

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: isDevSession
          ? "#d32f2f"
          : theme.palette.background.paper,
        color: isDevSession ? "#ffffff" : theme.palette.text.primary,
        borderBottom: `1px solid ${
          isDevSession ? "#b71c1c" : theme.palette.grey[200]
        }`,
        boxShadow: theme.shadows[1],
        zIndex: theme.zIndex.drawer + 1,
        ...sx,
      }}
    >
      <Toolbar sx={{ minHeight: 56 }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            cursor: "pointer",
          }}
        >
          <img
            src="/header-logo.svg"
            alt="Logo"
            style={{ height: 28, width: "auto" }}
            onClick={() => navigate("/")}
          />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "1.725rem" },
                  lineHeight: 1.2,
                  color: isDevSession ? "#ffffff" : "#006842",
                }}
                onClick={() => navigate("/")}
              >
                {title}
              </Typography>
              {isDevSession && (
                <Chip
                  label="DEV"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    backgroundColor: "#ffffff",
                    color: "#d32f2f",
                    border: "1px solid #ffffff",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: isDevSession
                    ? "#ffffff"
                    : theme.palette.text.secondary,
                  fontSize: "0.75rem",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {nav != null && (
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                flexDirection: "column",
                alignItems: "flex-end",
                lineHeight: 1.1,
                mr: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isDevSession
                    ? "rgba(255,255,255,0.85)"
                    : theme.palette.text.secondary,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                NAV
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: isDevSession ? "#ffffff" : "#006842",
                }}
              >
                {`$${nav.toLocaleString("en-US")}`}
              </Typography>
            </Box>
          )}
          <Tooltip title="Submit Help Ticket">
            {/* Icon-only on mobile, full button on desktop */}
            <Box>
              <IconButton
                onClick={() =>
                  window.open(
                    "https://forms.microsoft.com/r/Sq6aBjA5Sc",
                    "_blank",
                  )
                }
                sx={{
                  display: { xs: "flex", md: "none" },
                  color: isDevSession ? "#ffffff" : "#006842",
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<HelpOutlineIcon sx={{ fontSize: "1rem" }} />}
                onClick={() =>
                  window.open(
                    "https://forms.microsoft.com/r/Sq6aBjA5Sc",
                    "_blank",
                  )
                }
                disableElevation
                sx={{
                  display: { xs: "none", md: "flex" },
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  px: 2,
                  py: 0.6,
                  borderRadius: 5,
                  background: isDevSession
                    ? "rgba(255,255,255,0.2)"
                    : "linear-gradient(135deg, #006842 0%, #008B5A 100%)",
                  color: "#ffffff",
                  boxShadow: "0 2px 8px rgba(0, 104, 66, 0.25)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: isDevSession
                      ? "rgba(255,255,255,0.3)"
                      : "linear-gradient(135deg, #005535 0%, #006842 100%)",
                    boxShadow: "0 4px 12px rgba(0, 104, 66, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": { transform: "translateY(0px)" },
                }}
              >
                Submit Help Ticket
              </Button>
            </Box>
          </Tooltip>
          {actions && actions}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// Navigation Layout
export const NavigationLayout = ({
  children,
  drawerWidth = 280,
  items = navigationItems,
  title = "Glazer App",
  subtitle,
  actions,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        ...sx,
      }}
    >
      <ResponsiveAppBar
        onMenuClick={handleDrawerToggle}
        title={title}
        subtitle={subtitle}
        actions={actions}
      />
      {/* Spacer that exactly matches the AppBar height at every breakpoint */}
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, flexShrink: 0 }} />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <NavigationDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          drawerWidth={drawerOpen ? drawerWidth : 10}
          items={items}
        />
        <Box
          component="main"
          onClick={drawerOpen ? handleDrawerClose : undefined}
          sx={{
            flexGrow: 1,
            ml: isMobile ? 0 : drawerOpen ? `10px` : 0,
            transition: theme.transitions.create(["margin"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflow: "auto",
            backgroundColor: theme.palette.background.paper,
            width: "100%",
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default {
  NavigationDrawer,
  ResponsiveAppBar,
  NavigationLayout,
  navigationItems,
};
